import LinkIcon from '@mui/icons-material/Link'
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  TextField
} from '@mui/material'
import { TextSelection } from '@tiptap/pm/state'
import { MenuButton, useRichTextEditorContext } from 'mui-tiptap'
import { KeyboardEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '../../atoms'
import {
  ALLOWED_LINK_DOMAINS,
  ALLOWED_LINK_HOSTS,
  isAllowedLinkUrl,
  LINK_BUTTON_CLASS,
  normalizeLinkInput
} from './linkPolicy'

type LinkDraft = {
  text: string
  href: string
  asButton: boolean
  isEdit: boolean
  // Selected text when the dialog opened; unchanged text keeps its formatting
  originalText: string
}

const EMPTY_DRAFT: LinkDraft = {
  text: '',
  href: '',
  asButton: false,
  isEdit: false,
  originalText: ''
}

const allowedTargets = [...ALLOWED_LINK_DOMAINS, ...ALLOWED_LINK_HOSTS].join(', ')

/**
 * Toolbar button that opens a dialog to add, edit or remove a link. Only URLs
 * passing the link policy can be saved; links can optionally render as a button.
 */
export default function LinkMenuButton() {
  const editor = useRichTextEditorContext()
  const { t } = useTranslation('common')
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState<LinkDraft>(EMPTY_DRAFT)
  const [error, setError] = useState<string | null>(null)

  if (!editor) return null

  const handleOpen = () => {
    const isEdit = editor.isActive('link')
    // Select the whole link so editing replaces it instead of splitting it
    if (isEdit) editor.chain().extendMarkRange('link').run()

    const { from, to } = editor.state.selection
    const text = editor.state.doc.textBetween(from, to, ' ')
    const attrs = editor.getAttributes('link')

    setDraft({
      text,
      href: attrs.href ?? '',
      asButton: attrs.class === LINK_BUTTON_CLASS,
      isEdit,
      originalText: text
    })
    setError(null)
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
    editor.commands.focus()
  }

  const handleSave = () => {
    const href = normalizeLinkInput(draft.href)
    if (!isAllowedLinkUrl(href)) {
      setError(
        t(
          'richText.link.notAllowed',
          'Nur https-Links zu folgenden Domains sind erlaubt: {{domains}}',
          {
            domains: allowedTargets
          }
        )
      )
      return
    }

    const attrs = { href, class: draft.asButton ? LINK_BUTTON_CLASS : null }
    const label = draft.text.trim() || href
    const chain = editor.chain().focus()

    if (draft.originalText && label === draft.originalText) {
      chain.setLink(attrs)
    } else {
      chain.insertContent({ type: 'text', text: label, marks: [{ type: 'link', attrs }] })
    }
    chain
      // Collapse to the end of the link; text typed there must not continue it
      .command(({ tr }) => {
        tr.setSelection(TextSelection.create(tr.doc, tr.selection.to))
        return true
      })
      .unsetMark('link')
      .run()

    setOpen(false)
  }

  const handleRemove = () => {
    editor.chain().focus().extendMarkRange('link').unsetLink().run()
    setOpen(false)
  }

  // The editor usually lives inside a form; keep Enter from reaching it
  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      event.stopPropagation()
      // Same rule as the disabled save button
      if (draft.href.trim()) handleSave()
    }
  }

  return (
    <>
      <MenuButton
        tooltipLabel={t('richText.link.tooltip', 'Link einfügen')}
        aria-label={t('richText.link.tooltip', 'Link einfügen')}
        IconComponent={LinkIcon}
        selected={editor.isActive('link')}
        disabled={!editor.isEditable}
        onClick={handleOpen}
      />
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {draft.isEdit
            ? t('richText.link.editTitle', 'Link bearbeiten')
            : t('richText.link.addTitle', 'Link einfügen')}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label={t('richText.link.text', 'Text')}
              value={draft.text}
              onChange={event => setDraft(prev => ({ ...prev, text: event.target.value }))}
              onKeyDown={handleKeyDown}
              fullWidth
            />
            <TextField
              label={t('richText.link.url', 'URL')}
              value={draft.href}
              onChange={event => {
                setDraft(prev => ({ ...prev, href: event.target.value }))
                setError(null)
              }}
              onKeyDown={handleKeyDown}
              error={!!error}
              helperText={
                error ??
                t('richText.link.allowedHint', 'Erlaubt: {{domains}}', { domains: allowedTargets })
              }
              autoFocus
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={draft.asButton}
                  onChange={event =>
                    setDraft(prev => ({ ...prev, asButton: event.target.checked }))
                  }
                />
              }
              label={t('richText.link.asButton', 'Als Button darstellen')}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          {draft.isEdit && (
            <Button color="secondary" onClick={handleRemove} sx={{ mr: 'auto' }}>
              {t('richText.link.remove', 'Link entfernen')}
            </Button>
          )}
          <Button color="secondary" onClick={handleClose}>
            {t('common:abort')}
          </Button>
          <Button color="primary" onClick={handleSave} disabled={!draft.href.trim()}>
            {t('common:save')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
